import React, { forwardRef, useCallback, useEffect, useRef } from 'react';
import classNames from 'classnames';
import InfoMessage from './InfoMessage';

interface CheckboxInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
	label: string;
	description?: string;
	tooltip?: string;
	size?: 'sm' | 'md';
	/** Partial-selection state (dash). Ignored while checked. */
	indeterminate?: boolean;
}

const sizeClasses = {
	sm: {
		gap: 'gap-2',
		outer: 'w-5 h-5 border',
		inner: 'w-3 h-3',
		dash: 'peer-indeterminate:[&>div]:h-0.5',
		label: 'text-sm',
	},
	md: {
		gap: 'gap-3',
		outer: 'w-9 h-9 border-2',
		inner: 'w-6 h-6',
		dash: 'peer-indeterminate:[&>div]:h-1',
		label: '',
	},
} as const;

export const CheckboxInput = forwardRef<HTMLInputElement, CheckboxInputProps>(
	({ label, description, tooltip, className, disabled, size = 'md', indeterminate, ...props }, ref) => {
		const s = sizeClasses[size];
		// indeterminate is a DOM property, not an attribute — apply it via a ref.
		const inputRef = useRef<HTMLInputElement | null>(null);
		useEffect(() => {
			if (inputRef.current) {
				inputRef.current.indeterminate = !!indeterminate;
			}
		}, [indeterminate]);
		// Stable identity: an inline callback would make React detach/re-attach the ref on
		// every render of every checkbox, which is measurable on checkbox-heavy screens.
		const setRefs = useCallback((el: HTMLInputElement | null) => {
			inputRef.current = el;
			if (typeof ref === 'function') {
				ref(el);
			} else if (ref) {
				ref.current = el; // eslint-disable-line no-param-reassign
			}
		}, [ref]);
		return (
			<label
				className={classNames(
					'relative flex items-center group/checkbox',
					s.gap,
					disabled ? 'cursor-not-allowed' : 'cursor-pointer',
					className,
				)}
			>
				<input
					type="checkbox"
					ref={setRefs}
					// Replaced 'absolute opacity-0 pointer-events-none peer' with more robust hidden styles
					// to prevent document-level scroll jumps on focus, while keeping it accessible.
					className="absolute h-px w-px overflow-hidden whitespace-nowrap border-0 p-0 [clip:rect(0,0,0,0)] peer"
					disabled={disabled}
					{...props}
				/>
				<div
					className={classNames(
						'relative rounded transition-colors flex items-center justify-center flex-shrink-0',
						s.outer,
						'border-gray-300 bg-white group-hover/checkbox:border-cyan-400',
						'peer-checked:border-[#0180B5]',
						'peer-disabled:border-gray-300 peer-disabled:bg-gray-50',
						'peer-checked:[&>div]:bg-[#0180B5]',
						'peer-disabled:peer-checked:[&>div]:bg-gray-300',
						'peer-indeterminate:border-[#0180B5]',
						'peer-indeterminate:[&>div]:bg-[#0180B5]',
						'peer-disabled:peer-indeterminate:[&>div]:bg-gray-300',
						s.dash,
					)}
				>
					<div className={classNames(s.inner, 'bg-transparent rounded-sm transition-colors')} />
				</div>
				<div className="flex flex-col min-w-0 flex-1">
					<div className="flex items-center gap-1">
						<span className={classNames('text-gray-700 peer-disabled:text-gray-400', s.label)}>
							{label}
						</span>
						{tooltip && (
							<div className="relative group/tooltip">
								<span className="material-symbols-outlined text-gray-400 text-sm leading-none cursor-help">
									help
								</span>
								<div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 hidden group-hover/tooltip:block w-64 px-3 py-2 text-xs text-white bg-gray-800 rounded-md shadow-lg z-10 pointer-events-none">
									{tooltip}
								</div>
							</div>
						)}
					</div>
					{description && <InfoMessage className="mt-0">{description}</InfoMessage>}
				</div>
			</label>
		);
	},
);
